import axios from 'axios';
import { config } from '../../config/env.config';

/**
 * Axios instance configured for the API
 */
const apiClient = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Configure request interceptor to add authentication token
 */
export function setupApiInterceptors(msalInstance) {
  apiClient.interceptors.request.use(
    async (config) => {
      const account = msalInstance.getActiveAccount();
      
      if (account) {
        try {
          const response = await msalInstance.acquireTokenSilent({
            scopes: [import.meta.env.VITE_APP_ID_URI + "/.default"],
            account: account,
          });
          
          config.headers.Authorization = `Bearer ${response.accessToken}`;
        } catch (error) {
          console.error('Token acquisition failed:', error);
          // Token acquisition failed, user might need to login again
        }
      }
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Unauthorized - might need to re-authenticate
        console.error('Unauthorized request');
      }
      return Promise.reject(error);
    }
  );
}

/**
 * For requests with responseType 'blob', failed HTTP responses still arrive as a Blob.
 * Read as text so callers can show the real API error (e.g. Azure 500 body).
 */
export async function getAxiosErrorDetail(error) {
  const res = error?.response;
  if (!res?.data) {
    return error?.message ?? 'Request failed';
  }
  const data = res.data;
  if (typeof data === 'string') {
    return data;
  }
  if (data instanceof Blob) {
    const text = await data.text();
    const ct = res.headers?.['content-type'] ?? '';
    if (ct.includes('application/json')) {
      try {
        const j = JSON.parse(text);
        return j.message ?? j.Message ?? j.error ?? j.title ?? text;
      } catch {
        return text;
      }
    }
    return text || error?.message || 'Request failed';
  }
  return error?.message ?? 'Request failed';
}

export default apiClient;

